/**
 * The one HTTP client every screen goes through.
 *
 * It owns four things the contract (contracts/partner-api.md §Conventions)
 * makes a property of the whole surface, so no screen can get them wrong:
 *
 *  - **Version pinning.** `X-Partner-Api-Version` on every request.
 *  - **Idempotency.** Every write carries an `X-Idempotency-Key` minted once
 *    per logical operation. A transparent retry of that operation (after an
 *    access-token refresh) reuses the SAME key, so the server's replay cache
 *    answers it instead of performing the write twice — the case that matters
 *    for `rotate-secret`, where a doubled write burns the previous-secret slot.
 *    Auth endpoints are excluded: the server does not honour the header there,
 *    and a refresh token is single-use by design.
 *  - **Session refresh.** Access tokens live an hour and refresh tokens rotate
 *    on use with reuse detection, so two concurrent refreshes would sign the
 *    partner out everywhere. Refresh is single-flight: every request that needs
 *    a fresh token awaits the same promise.
 *  - **Errors.** Every non-2xx becomes an `ApiError` with a stable `code` and a
 *    human message (errors.ts); a network failure is `NETWORK_ERROR`.
 */
import { API_BASE, PARTNER_API_VERSION } from '../env';
import { ApiError } from './errors';

export interface Tokens {
  access_token: string;
  refresh_token: string;
}

export interface TokenStore {
  getAccess(): string | null;
  getRefresh(): string | null;
  set(tokens: Tokens): void;
  clear(): void;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Send the bearer token (default true). */
  auth?: boolean;
  /** Override the minted idempotency key (tests; resuming a known operation). */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** A multipart body (`uploadForm`), sent as-is: the browser sets its Content-Type and boundary. */
  form?: FormData;
}

export interface ApiClientOptions {
  baseUrl?: string;
  store: TokenStore;
  fetchImpl?: typeof fetch;
  newKey?: () => string;
  /** Seconds before `exp` at which a token counts as expired (default 30). */
  skewSeconds?: number;
  now?: () => number;
  /** Called once when the session cannot be recovered (refresh refused). */
  onSessionEnded?: (error: ApiError) => void;
}

const SESSION_ENDING_CODES = new Set([
  'TOKEN_EXPIRED',
  'REFRESH_REUSE_DETECTED',
  'ACCOUNT_DISABLED',
  'SESSION_REVOKED',
  'UNAUTHENTICATED',
  'MEMBERSHIP_REQUIRED',
]);

/** Reads `exp` (seconds) out of a JWT without verifying it — scheduling only. */
export function jwtExpiry(token: string | null): number | null {
  if (!token) return null;
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === 'number' ? exp : null;
  } catch {
    return null;
  }
}

function defaultKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

export class ApiClient {
  readonly baseUrl: string;
  private readonly store: TokenStore;
  private readonly fetchImpl: typeof fetch;
  private readonly newKey: () => string;
  private readonly skew: number;
  private readonly now: () => number;
  private readonly onSessionEnded?: (error: ApiError) => void;
  private refreshing: Promise<void> | null = null;

  constructor(options: ApiClientOptions) {
    this.baseUrl = (options.baseUrl ?? API_BASE).replace(/\/+$/, '');
    this.store = options.store;
    this.fetchImpl = options.fetchImpl ?? ((...args) => fetch(...args));
    this.newKey = options.newKey ?? defaultKey;
    this.skew = options.skewSeconds ?? 30;
    this.now = options.now ?? (() => Date.now());
    this.onSessionEnded = options.onSessionEnded;
  }

  get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('GET', path, options);
  }
  post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('POST', path, { ...options, body });
  }
  put<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('PUT', path, { ...options, body });
  }
  patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('PATCH', path, { ...options, body });
  }
  delete<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('DELETE', path, options);
  }
  /**
   * Multipart POST (a hosted page photo). The same version pin, bearer token,
   * refresh-and-replay and idempotency key as every other write; no
   * Content-Type is set, so the browser adds the multipart boundary.
   */
  uploadForm<T>(path: string, form: FormData, options: Omit<RequestOptions, 'body' | 'form'> = {}): Promise<T> {
    return this.request<T>('POST', path, { ...options, form });
  }

  /** True when an access token exists and is not about to expire. */
  hasFreshAccess(): boolean {
    const exp = jwtExpiry(this.store.getAccess());
    return exp !== null && exp * 1000 - this.skew * 1000 > this.now();
  }

  async request<T>(method: Method, path: string, options: RequestOptions = {}): Promise<T> {
    const auth = options.auth ?? true;
    const isWrite = method !== 'GET';
    const isAuthEndpoint = path.startsWith('/partner/auth/');
    // Minted ONCE here, outside the retry loop below, so a retry after a
    // refresh replays the same logical operation.
    const idempotencyKey = isWrite && !isAuthEndpoint ? (options.idempotencyKey ?? this.newKey()) : undefined;

    if (auth && !this.hasFreshAccess() && this.store.getRefresh()) {
      await this.refresh();
    }

    const sentWith = this.store.getAccess();
    let response = await this.send(method, path, options, auth, idempotencyKey);
    if (response.status === 401 && auth && this.store.getRefresh()) {
      const body = await readJson(response);
      const code = typeof body.code === 'string' ? body.code : '';
      // The session was REPLACED while this request was in flight — choosing
      // an organisation revokes the unscoped sign-in it was traded for — so
      // the 401 is about the old token, not the session we hold now. Replay
      // once on the new token rather than ending the new session.
      if (sentWith !== this.store.getAccess()) {
        response = await this.send(method, path, options, auth, idempotencyKey);
      } else if (code === 'INVALID_TOKEN') {
        // Only an expired/invalid ACCESS token is worth a refresh. A revoked
        // session family is final: refreshing it would fail the same way.
        await this.refresh();
        response = await this.send(method, path, options, auth, idempotencyKey);
      } else {
        return this.fail(response.status, body);
      }
    }
    if (response.status === 204) return undefined as T;
    const body = await readJson(response);
    if (!response.ok) return this.fail(response.status, body);
    return body as T;
  }

  /** Authenticated binary GET (the CSV export download). */
  async download(path: string): Promise<Blob> {
    if (!this.hasFreshAccess() && this.store.getRefresh()) await this.refresh();
    const response = await this.send('GET', path, {}, true, undefined);
    if (!response.ok) return this.fail(response.status, await readJson(response));
    return response.blob();
  }

  /** Single-flight refresh. Concurrent callers share one network request. */
  refresh(): Promise<void> {
    if (!this.refreshing) {
      this.refreshing = this.doRefresh().finally(() => {
        this.refreshing = null;
      });
    }
    return this.refreshing;
  }

  private async doRefresh(): Promise<void> {
    const refreshToken = this.store.getRefresh();
    if (!refreshToken) {
      const error = new ApiError({ status: 401, code: 'UNAUTHENTICATED' });
      this.endSession(error);
      throw error;
    }
    const response = await this.send('POST', '/partner/auth/refresh', { body: { refresh_token: refreshToken } }, false, undefined);
    const body = await readJson(response);
    if (!response.ok) {
      const error = ApiError.fromResponse(response.status, body);
      if (SESSION_ENDING_CODES.has(error.code) || response.status === 401 || response.status === 403) {
        this.endSession(error);
      }
      throw error;
    }
    this.store.set({ access_token: String(body.access_token), refresh_token: String(body.refresh_token) });
  }

  private endSession(error: ApiError): void {
    this.store.clear();
    this.onSessionEnded?.(error);
  }

  private fail(status: number, body: Record<string, unknown>): never {
    const error = ApiError.fromResponse(status, body);
    if (status === 401 && SESSION_ENDING_CODES.has(error.code)) this.endSession(error);
    throw error;
  }

  private async send(
    method: Method,
    path: string,
    options: RequestOptions,
    auth: boolean,
    idempotencyKey: string | undefined,
  ): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Partner-Api-Version': PARTNER_API_VERSION,
    };
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';
    if (idempotencyKey) headers['X-Idempotency-Key'] = idempotencyKey;
    const access = this.store.getAccess();
    if (auth && access) headers.Authorization = `Bearer ${access}`;
    try {
      return await this.fetchImpl(this.url(path, options.query), {
        method,
        headers,
        body: options.form ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
        signal: options.signal,
        credentials: 'omit',
      });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      throw new ApiError({ status: 0, code: 'NETWORK_ERROR' });
    }
  }

  url(path: string, query?: RequestOptions['query']): string {
    const url = `${this.baseUrl}${path}`;
    if (!query) return url;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      params.set(key, String(value));
    }
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
  }
}

async function readJson(response: Response): Promise<Record<string, unknown>> {
  const text = await response.text().catch(() => '');
  if (!text) return {};
  try {
    const parsed: unknown = JSON.parse(text);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/** localStorage-backed store: refresh token persists (30 d), access in memory. */
export function createBrowserTokenStore(storageKey = 'fx-partner-session'): TokenStore {
  let access: string | null = null;
  const read = (): string | null => {
    try {
      return window.localStorage.getItem(storageKey);
    } catch {
      return null;
    }
  };
  return {
    getAccess: () => access,
    getRefresh: read,
    set(tokens) {
      access = tokens.access_token;
      try {
        window.localStorage.setItem(storageKey, tokens.refresh_token);
      } catch {
        /* private mode: the session simply does not survive a reload */
      }
    },
    clear() {
      access = null;
      try {
        window.localStorage.removeItem(storageKey);
      } catch {
        /* ignore */
      }
    },
  };
}
